import {
  ClinicSlugSchema,
  type OpenPublicDocumentRequest,
  OpenPublicDocumentRequestSchema,
  type PublicDocumentSession,
  PublicDocumentSessionSchema,
} from "@emr/contracts";

export const documentLinkUnavailable =
  "This link could not be opened. Check your date of birth. If it is right, the link may have expired or been replaced: ask the clinic for a new one.";
export const documentClinicUnreachable =
  "The clinic could not be reached. Check your connection and try again.";

export class DocumentLinkUnavailableError extends Error {
  public constructor() {
    super(documentLinkUnavailable);
    this.name = "DocumentLinkUnavailableError";
  }
}

/** Only the POST body contains the credential and date of birth; no cookie or browser cache. */
async function request(
  clinicSlug: string,
  path: "open" | "file",
  input: OpenPublicDocumentRequest,
  signal: AbortSignal,
): Promise<Response> {
  const slug = ClinicSlugSchema.parse(clinicSlug);
  const body = OpenPublicDocumentRequestSchema.parse(input);
  const origin = (import.meta.env.VITE_AGENT_API_ORIGIN || "http://127.0.0.1:4100").replace(
    /\/$/,
    "",
  );
  const response = await fetch(`${origin}/v1/public/${slug}/documents/${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal,
    cache: "no-store",
    credentials: "omit",
    referrerPolicy: "no-referrer",
  });
  if (response.status === 404) throw new DocumentLinkUnavailableError();
  if (!response.ok) throw new Error(documentClinicUnreachable);
  return response;
}

export async function openPublicDocument(
  clinicSlug: string,
  input: OpenPublicDocumentRequest,
  signal: AbortSignal,
): Promise<PublicDocumentSession> {
  return PublicDocumentSessionSchema.parse(
    await (await request(clinicSlug, "open", input, signal)).json(),
  );
}

export async function readPublicDocument(
  clinicSlug: string,
  input: OpenPublicDocumentRequest,
  byteSize: number,
  signal: AbortSignal,
): Promise<Blob> {
  const response = await request(clinicSlug, "file", input, signal);
  if (response.headers.get("content-type")?.split(";", 1)[0]?.trim() !== "application/pdf") {
    throw new Error(documentClinicUnreachable);
  }
  const bytes = await response.arrayBuffer();
  if (bytes.byteLength !== byteSize) throw new Error(documentClinicUnreachable);
  return new Blob([bytes], { type: "application/pdf" });
}
